(function($){
    'use strict';
    (() => {
        class App {
            static init() {
                App.initEase();
                App.initSeasonToggle();
                App.initDesktopToggle();
                App.initNavDropdowns();
                App.initMobileNav();
                App.initHeaderScroll();
                App.initLangSwitcher();
                App.initMarquee();
                App.initCardSliders();
                App.initStatusBar();
                App.initTrailStatuses();
                App.initPostsList();
                App.initCalendars();
                App.initImageCarousels();
                App.initReviewsMarquee();
                App.initWeather();
                App.initAccordions();
                App.initTrailCards();
            }

            /**
             * Fade-in animation for .ease elements.
             * Adds .show when element scrolls into view.
             */
            static initEase() {
                const els = document.querySelectorAll('.ease');
                if (!els.length) return;

                if (!('IntersectionObserver' in window)) {
                    els.forEach(el => el.classList.add('show'));
                    return;
                }

                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            entry.target.classList.add('show');
                            observer.unobserve(entry.target);
                        }
                    });
                    // Threshold 0 + rootMargin pulls the trigger forward so any
                    // element that even touches the viewport (or is within
                    // 100px of entering it) reveals itself. The previous 0.1
                    // threshold meant tall sections like article bodies needed
                    // to be 10% in-frame before showing — on long posts that
                    // looked like the body refused to render until you scrolled.
                }, { threshold: 0, rootMargin: '0px 0px 100px 0px' });

                els.forEach(el => observer.observe(el));
            }

            /**
             * Season toggle — sets cookie and redirects to the correct page.
             * Handles multiple toggle buttons (desktop header + mobile nav).
             */
            static initSeasonToggle() {
                const btns = document.querySelectorAll('[data-season-toggle]');
                if (!btns.length) return;

                btns.forEach(btn => {
                    btn.addEventListener('click', () => {
                        const current = document.body.dataset.season || 'winter';
                        const next = current === 'winter' ? 'summer' : 'winter';

                        const vars = window.lmar_vars || {};
                        const winterHome = vars.winter_home ? new URL(vars.winter_home).pathname : '/';
                        const summerHome = vars.summer_home ? new URL(vars.summer_home).pathname : '/summer/';
                        const targetPath = next === 'summer' ? summerHome : winterHome;

                        // Always land on the other season's home page. ?season=<next>
                        // is carried so the server can Set-Cookie for the choice —
                        // a Safari-safe alternative to document.cookie writes that
                        // race a synchronous navigation.
                        const url = new URL(targetPath, window.location.origin);
                        url.searchParams.set('season', next);
                        window.location.href = url.toString();
                    });
                });

            }

            /**
             * Desktop season toggle — measures both states and animates width + opacity on hover.
             */
            static initDesktopToggle() {
                document.querySelectorAll('.season-toggle--desktop').forEach(toggle => {
                    const def   = toggle.querySelector('.season-toggle__default');
                    const hover = toggle.querySelector('.season-toggle__hover');
                    if (!def || !hover) return;

                    const season    = document.body.dataset.season || 'winter';
                    const hoverBg   = season === 'winter' ? '#374219' : '#131852';
                    const defaultBg = season === 'winter' ? '#131852' : '#374219';

                    // Temporarily make spans relative to measure natural widths
                    def.style.position = 'relative';
                    hover.style.position = 'relative';
                    const defW   = def.offsetWidth;
                    const hoverW = hover.offsetWidth;
                    def.style.position = '';
                    hover.style.position = '';

                    // Set initial width to default size
                    toggle.style.width = defW + 'px';

                    toggle.addEventListener('mouseenter', () => {
                        toggle.style.width = hoverW + 'px';
                        toggle.style.backgroundColor = hoverBg;
                        def.style.opacity = '0';
                        hover.style.opacity = '1';
                    });

                    toggle.addEventListener('mouseleave', () => {
                        toggle.style.width = defW + 'px';
                        toggle.style.backgroundColor = defaultBg;
                        def.style.opacity = '1';
                        hover.style.opacity = '0';
                    });
                });
            }

            /**
             * Desktop nav dropdowns — JS hover with delay for clean UX.
             * Opens on mouseenter of the <a>, stays open while hovering the sub-menu,
             * closes with a short delay when mouse leaves both.
             */
            static initNavDropdowns() {
                const items = document.querySelectorAll('.header__nav > ul > li.menu-item-has-children');
                if (!items.length) return;

                items.forEach(li => {
                    let timer = null;
                    let closeTimer = null;
                    let isOverSub = false;

                    const open = () => {
                        clearTimeout(closeTimer);
                        items.forEach(other => {
                            if (other !== li) other.classList.remove('is-open');
                        });
                        li.classList.add('is-open');
                    };

                    const close = () => {
                        if (isOverSub) return;
                        li.classList.remove('is-open');
                    };

                    const scheduleClose = () => {
                        clearTimeout(closeTimer);
                        closeTimer = setTimeout(close, 200);
                    };

                    // Only the <a> can open the dropdown
                    const link = li.querySelector(':scope > a');
                    if (link) {
                        link.addEventListener('mouseenter', open);
                        link.addEventListener('mouseleave', scheduleClose);
                    }

                    // Sub-menu keeps it open / closes on leave
                    const sub = li.querySelector(':scope > .sub-menu');
                    if (sub) {
                        sub.addEventListener('mouseenter', () => {
                            isOverSub = true;
                            clearTimeout(closeTimer);
                        });
                        sub.addEventListener('mouseleave', () => {
                            isOverSub = false;
                            scheduleClose();
                        });
                    }
                });
            }

            /**
             * Header scroll behaviour.
             * Homepage (no data-subpage attr): starts as page bg → white + shadow on scroll.
             * Sub-pages (data-subpage attr): CSS sets white + shadow immediately; scroll handler skipped.
             */
            static initHeaderScroll() {
                const header = document.getElementById('header');
                if (!header || header.hasAttribute('data-subpage')) return;

                const threshold = 10;

                const update = () => {
                    if (window.scrollY > threshold) {
                        header.classList.add('is-scrolled');
                    } else {
                        header.classList.remove('is-scrolled');
                    }
                };

                window.addEventListener('scroll', update, { passive: true });
                update(); // run on load in case page is already scrolled
            }

            /**
             * Mobile nav — hamburger toggle + accordion sub-menus.
             */
            static initMobileNav() {
                const btns   = document.querySelectorAll('[data-hamburger]');
                const drawer = document.getElementById('mobile-nav');
                if (!btns.length || !drawer) return;

                const headerBtn = document.querySelector('.header__hamburger');

                const open = () => {
                    drawer.classList.add('is-open');
                    drawer.setAttribute('aria-hidden', 'false');
                    if (headerBtn) {
                        headerBtn.classList.add('is-active');
                        headerBtn.setAttribute('aria-expanded', 'true');
                    }
                    document.body.classList.add('no-scroll');
                };

                const close = () => {
                    drawer.classList.remove('is-open');
                    drawer.setAttribute('aria-hidden', 'true');
                    if (headerBtn) {
                        headerBtn.classList.remove('is-active');
                        headerBtn.setAttribute('aria-expanded', 'false');
                    }
                    document.body.classList.remove('no-scroll');
                };

                btns.forEach(btn => {
                    btn.addEventListener('click', () => {
                        drawer.classList.contains('is-open') ? close() : open();
                    });
                });

                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
                });

                // Close if viewport grows past mobile breakpoint
                const mq = window.matchMedia('(min-width: 1300px)');
                mq.addEventListener('change', (e) => { if (e.matches) close(); });

                // Build accordion: inject + buttons into top-level items with sub-menus
                App.initMobileNavAccordion(drawer);
            }

            /**
             * Accordion for mobile nav sub-menus.
             * Injects a toggle button into each parent list item.
             */
            static initMobileNavAccordion(drawer) {
                const menu = drawer.querySelector('.mobile-nav__menu');
                if (!menu) return;

                const parentItems = menu.querySelectorAll('li.menu-item-has-children');

                parentItems.forEach(li => {
                    // Skip level-2 heading items inside a mega-menu — they're hidden via CSS
                    if (li.closest('li.mega-menu') && !li.classList.contains('mega-menu')) return;

                    const link    = li.querySelector(':scope > a');
                    const submenu = li.querySelector(':scope > .sub-menu');
                    if (!link || !submenu) return;

                    // Wrap link + button in a row div
                    const row = document.createElement('div');
                    row.className = 'mobile-nav__item-row';
                    li.insertBefore(row, link);
                    row.appendChild(link);

                    // Create + toggle button
                    const toggleBtn = document.createElement('button');
                    toggleBtn.className = 'mobile-nav__accordion-btn';
                    toggleBtn.setAttribute('aria-expanded', 'false');
                    toggleBtn.setAttribute('aria-label', 'Expand submenu');
                    toggleBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="accordion-icon"><rect x="0" y="7.15638" width="19.79" height="5.23732" rx="2" fill="#F0FAFF"/><rect class="accordion-icon__v" x="7.27632" y="0" width="5.23738" height="19.4302" rx="2" fill="#F0FAFF"/></svg>';
                    row.appendChild(toggleBtn);

                    const closeSubmenu = (sm, btn) => {
                        sm.style.height = sm.scrollHeight + 'px';
                        requestAnimationFrame(() => {
                            sm.style.height = '0';
                        });
                        sm.classList.remove('is-open');
                        btn.classList.remove('is-open');
                        btn.setAttribute('aria-expanded', 'false');
                    };

                    const openSubmenu = (sm, btn) => {
                        sm.style.height = '0';
                        sm.classList.add('is-open');
                        sm.style.height = sm.scrollHeight + 'px';
                        sm.addEventListener('transitionend', () => {
                            sm.style.height = 'auto';
                        }, { once: true });
                        btn.classList.add('is-open');
                        btn.setAttribute('aria-expanded', 'true');
                    };

                    const toggle = () => {
                        const isOpen = submenu.classList.contains('is-open');

                        // Close all other open submenus first
                        menu.querySelectorAll('.sub-menu.is-open').forEach(openSm => {
                            if (openSm !== submenu) {
                                const openBtn = openSm.closest('li').querySelector('.mobile-nav__accordion-btn');
                                if (openBtn) closeSubmenu(openSm, openBtn);
                            }
                        });

                        isOpen ? closeSubmenu(submenu, toggleBtn) : openSubmenu(submenu, toggleBtn);
                    };

                    toggleBtn.addEventListener('click', toggle);

                    // If the link has no real destination, make the whole row toggle
                    const href = link.getAttribute('href');
                    if (!href || href === '#') {
                        link.addEventListener('click', (e) => {
                            e.preventDefault();
                            toggle();
                        });
                    }
                });
            }

            /**
             * Card sliders — Slick on mobile only, destroyed at md+ breakpoint.
             * Reinitialised if viewport shrinks back below breakpoint.
             */
            static initCardSliders() {
                const $sliders = $('.js-cards-slider');
                if (!$sliders.length || typeof $.fn.slick === 'undefined') return;

                const slickSettings = {
                    slidesToShow: 4,
                    slidesToScroll: 1,
                    arrows: false,
                    dots: false,
                    infinite: false,
                    swipeToSlide: true,
                    responsive: [
                        {
                            breakpoint: 1200,
                            settings: { slidesToShow: 3 },
                        },
                        {
                            breakpoint: 1024,
                            settings: { slidesToShow: 2 },
                        },
                        {
                            breakpoint: 600,
                            settings: { slidesToShow: 1 },
                        },
                    ],
                };

                const applyDescPadding = ($slider) => {
                    const items = $slider.find('.card-item').toArray();
                    const slides = $slider.find('.slick-slide');

                    // 1. Reset padding and temporarily collapse flex
                    items.forEach(item => {
                        const desc = item.querySelector('.card-item__desc');
                        if (desc) {
                            desc.style.padding = '0';
                            desc.style.flex = '0 0 auto';
                        }
                        item.style.height = 'auto';
                    });
                    slides.css('height', 'auto').children().css('height', 'auto');

                    // 2. Force reflow then measure natural body heights
                    void $slider[0].offsetHeight;
                    let maxH = 0;
                    const measured = items.map(item => {
                        const body = item.querySelector('.card-item__body');
                        const h = body ? body.offsetHeight : 0;
                        if (h > maxH) maxH = h;
                        return { item, h };
                    });

                    // 3. Restore flex and heights
                    items.forEach(item => {
                        const desc = item.querySelector('.card-item__desc');
                        if (desc) desc.style.flex = '';
                        item.style.height = '';
                    });
                    slides.css('height', '').children().css('height', '');

                    // 4. Apply padding only to the tallest card(s)
                    measured.forEach(({ item, h }) => {
                        if (h >= maxH) {
                            const desc = item.querySelector('.card-item__desc');
                            if (desc) desc.style.padding = '32px 0';
                        }
                    });
                };

                // Toggle .is-at-end on the section when the rightmost slide is
                // in view, so the SCSS can pull the slick-list right and snug
                // the trailing card up to the right edge.
                const updateEndState = ($s, current) => {
                    const $section = $s.closest('.cards-section, .news-feed');
                    if (!$section.length) return;
                    const activeCount  = $s.find('.slick-active').length;
                    const totalSlides  = $s.find('.slick-slide:not(.slick-cloned)').length;
                    if (!activeCount || !totalSlides) return;
                    const lastActive   = $s.find('.slick-active').last();
                    const lastIdx      = parseInt(lastActive.attr('data-slick-index'), 10);
                    const isAtEnd = (typeof current === 'number')
                        ? current + activeCount >= totalSlides
                        : (Number.isFinite(lastIdx) ? lastIdx >= totalSlides - 1 : false);
                    $section.toggleClass('is-at-end', isAtEnd);
                };

                $sliders.each(function () {
                    const $s = $(this);
                    // 'init' must be bound before .slick() so the handler is
                    // registered when slick fires it. beforeChange fires at
                    // the start of the transition so the translate animates
                    // alongside Slick's track movement rather than snapping
                    // after.
                    $s.on('init reInit',    () => updateEndState($s));
                    $s.on('breakpoint',     () => updateEndState($s));
                    $s.on('afterChange',    (e, _slick, current) => updateEndState($s, current));
                    $s.on('beforeChange',   (e, _slick, _curr, next) => updateEndState($s, next));
                    $s.slick(slickSettings);
                    applyDescPadding($s);
                    // Belt-and-braces in case init fired before our handler.
                    updateEndState($s);
                });
            }

            /**
             * Language switcher — toggle dropdown open/closed, close on outside click.
             */
            static initLangSwitcher() {
                const switchers = document.querySelectorAll('.lang-switcher');
                if (!switchers.length) return;

                switchers.forEach(switcher => {
                    const trigger  = switcher.querySelector('.lang-switcher__trigger');
                    const dropdown = switcher.querySelector('.lang-switcher__dropdown');
                    if (!trigger || !dropdown) return;

                    trigger.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

                        // Close all other switchers first
                        document.querySelectorAll('.lang-switcher__trigger[aria-expanded="true"]').forEach(t => {
                            if (t !== trigger) {
                                t.setAttribute('aria-expanded', 'false');
                                t.closest('.lang-switcher').querySelector('.lang-switcher__dropdown').hidden = true;
                            }
                        });

                        trigger.setAttribute('aria-expanded', String(!isOpen));
                        dropdown.hidden = isOpen;
                    });
                });

                // Close on outside click
                document.addEventListener('click', () => {
                    document.querySelectorAll('.lang-switcher__trigger[aria-expanded="true"]').forEach(t => {
                        t.setAttribute('aria-expanded', 'false');
                        t.closest('.lang-switcher').querySelector('.lang-switcher__dropdown').hidden = true;
                    });
                });

                // Close on Escape
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape') {
                        document.querySelectorAll('.lang-switcher__trigger[aria-expanded="true"]').forEach(t => {
                            t.setAttribute('aria-expanded', 'false');
                            t.closest('.lang-switcher').querySelector('.lang-switcher__dropdown').hidden = true;
                        });
                    }
                });
            }

            /**
             * Marquee — rAF-driven seamless scroll.
             * Clones sets until there's enough to cover 2× viewport, then translates
             * the track, resetting cleanly when one full set has passed.
             */
            static initMarquee() {
                document.querySelectorAll('.js-marquee').forEach(wrap => {
                    const track = wrap.querySelector('.marquee__track');
                    if (!track) return;

                    const sets = track.querySelectorAll('.marquee__set');
                    if (!sets.length) return;

                    const firstSet = sets[0];
                    const section  = wrap.closest('.marquee');
                    // Speed in pixels per second (from --marquee-speed, default 30)
                    const speed = parseFloat(section?.style.getPropertyValue('--marquee-speed')) || 30;

                    // Flip translation sign in RTL so the track loops in the
                    // natural reading direction instead of dragging off-screen.
                    const dirSign = getComputedStyle(track).direction === 'rtl' ? 1 : -1;

                    let offset   = 0;
                    let lastTime = 0;
                    let setWidth = 0;
                    let running  = true;

                    const measure = () => {
                        setWidth = firstSet.offsetWidth;
                    };

                    const ensureFill = () => {
                        measure();
                        if (setWidth === 0) return;
                        const needed = Math.ceil((window.innerWidth * 2) / setWidth) + 1;
                        const current = track.querySelectorAll('.marquee__set').length;
                        for (let i = current; i < needed; i++) {
                            const clone = firstSet.cloneNode(true);
                            clone.setAttribute('aria-hidden', 'true');
                            track.appendChild(clone);
                        }
                    };

                    ensureFill();
                    measure();

                    const tick = (timestamp) => {
                        if (!running) return;
                        if (!lastTime) lastTime = timestamp;
                        const delta = (timestamp - lastTime) / 1000;
                        lastTime = timestamp;

                        offset += speed * delta;
                        if (setWidth > 0 && offset >= setWidth) {
                            offset -= setWidth;
                        }

                        track.style.transform = `translate3d(${dirSign * offset}px, 0, 0)`;
                        requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);

                    let resizeTimer;
                    window.addEventListener('resize', () => {
                        clearTimeout(resizeTimer);
                        resizeTimer = setTimeout(() => {
                            ensureFill();
                            measure();
                        }, 200);
                    });

                    wrap.addEventListener('mouseenter', () => { running = false; });
                    wrap.addEventListener('mouseleave', () => {
                        running  = true;
                        lastTime = 0;
                        requestAnimationFrame(tick);
                    });
                });
            }

            /**
             * Status bar — rAF-driven seamless scroll (same pattern as marquee).
             */
            static initStatusBar() {
                document.querySelectorAll('.js-status-bar').forEach(bar => {
                    const track = bar.querySelector('.status-bar__track');
                    if (!track) return;
                    const sets = track.querySelectorAll('.status-bar__set');
                    if (!sets.length) return;

                    const firstSet = sets[0];
                    const speed    = 80; // px/sec

                    // Flip translation sign in RTL so the track loops in the
                    // natural reading direction instead of dragging off-screen.
                    const dirSign = getComputedStyle(track).direction === 'rtl' ? 1 : -1;

                    let offset   = 0;
                    let lastTime = 0;
                    let setWidth = 0;
                    let running  = true;

                    const measure = () => { setWidth = firstSet.offsetWidth; };

                    const ensureFill = () => {
                        measure();
                        if (setWidth === 0) return;
                        const needed = Math.ceil((window.innerWidth * 2) / setWidth) + 1;
                        const current = track.querySelectorAll('.status-bar__set').length;
                        for (let i = current; i < needed; i++) {
                            const clone = firstSet.cloneNode(true);
                            clone.setAttribute('aria-hidden', 'true');
                            track.appendChild(clone);
                        }
                    };

                    const createItem = (item) => {
                        const el    = document.createElement('span');
                        const label = document.createElement('span');
                        el.className = 'status-bar__item';
                        el.setAttribute('data-status-bar-live', '');
                        label.className = 'status-bar__label';
                        label.textContent = item.label || '';
                        el.appendChild(label);

                        if (item.value) {
                            const value = document.createElement('span');
                            value.className = 'status-bar__value';
                            value.textContent = item.value;
                            el.appendChild(value);
                        }

                        return el;
                    };

                    const refreshLiveItems = () => {
                        const vars = window.ajax_var || {};
                        if (!vars.ppd_ajax_url) return;

                        $.ajax({
                            url:   vars.ppd_ajax_url,
                            type:  'POST',
                            cache: false,
                            data:  {
                                action: 'lmar_status_bar_data',
                                season: bar.dataset.season || '',
                                _: Date.now(),
                            },
                        }).done(res => {
                            if (!res || !res.success || !res.data) return;

                            track.querySelectorAll('.status-bar__set:not(:first-child)').forEach(set => set.remove());
                            firstSet.querySelectorAll('[data-status-bar-live]').forEach(item => item.remove());

                            const firstManualItem = firstSet.querySelector('.status-bar__item');
                            (res.data.before || []).forEach(item => {
                                firstSet.insertBefore(createItem(item), firstManualItem);
                            });
                            (res.data.after || []).forEach(item => firstSet.appendChild(createItem(item)));

                            offset = 0;
                            track.style.transform = 'translate3d(0, 0, 0)';
                            ensureFill();
                            measure();
                        });
                    };

                    ensureFill();
                    measure();
                    refreshLiveItems();

                    const tick = (timestamp) => {
                        if (!running) return;
                        if (!lastTime) lastTime = timestamp;
                        const delta = (timestamp - lastTime) / 1000;
                        lastTime = timestamp;

                        offset += speed * delta;
                        if (setWidth > 0 && offset >= setWidth) offset -= setWidth;

                        track.style.transform = `translate3d(${dirSign * offset}px, 0, 0)`;
                        requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);

                    let resizeTimer;
                    window.addEventListener('resize', () => {
                        clearTimeout(resizeTimer);
                        resizeTimer = setTimeout(() => {
                            ensureFill();
                            measure();
                        }, 200);
                    });
                });
            }

            /**
             * Trail statuses — refresh status pills after page load so cached
             * module HTML cannot show stale open/closed values.
             */
            static initTrailStatuses() {
                const pills = Array.from(document.querySelectorAll('[data-trail-status-id]'));
                if (!pills.length) return;

                const vars = window.ajax_var || {};
                if (!vars.ppd_ajax_url) return;

                const ids = [...new Set(pills.map(pill => pill.dataset.trailStatusId).filter(Boolean))];
                if (!ids.length) return;

                $.ajax({
                    url:   vars.ppd_ajax_url,
                    type:  'POST',
                    cache: false,
                    data:  {
                        action:    'lmar_trail_statuses',
                        trail_ids: ids,
                        _:         Date.now(),
                    },
                }).done(res => {
                    if (!res || !res.success || !res.data) return;

                    pills.forEach(pill => {
                        const item = res.data[pill.dataset.trailStatusId];
                        if (!item) return;

                        const prefix = pill.classList.contains('trail-card__status')
                            ? 'trail-card__status--'
                            : 'status-col__item-status--';

                        Array.from(pill.classList).forEach(className => {
                            if (className.startsWith(prefix)) pill.classList.remove(className);
                        });
                        pill.classList.add(prefix + item.status);
                        pill.textContent = item.label;
                    });
                });
            }

            /**
             * Posts List — category filters + AJAX "Load More".
             */
            static initPostsList() {
                document.querySelectorAll('.js-posts-list').forEach(module => {
                    const grid       = module.querySelector('.js-posts-list-grid');
                    const loadBtn    = module.querySelector('.js-posts-list-load-more');
                    const footer     = module.querySelector('.posts-list__footer');
                    const filterBtns = module.querySelectorAll('.posts-list__filter');
                    const count      = parseInt(module.dataset.count, 10) || 8;
                    const season     = module.dataset.season || 'all';

                    if (!grid) return;

                    const vars = window.ajax_var || {};
                    // PHP renders the matching pill is-active and pre-filters
                    // the first batch when ?category=<slug> is on the URL, so
                    // we just mirror that state here.
                    let currentFilter = module.dataset.activeFilter || 'all';
                    let isLoading = false;

                    const fetchPosts = (offset, replace) => {
                        if (isLoading) return;
                        isLoading = true;
                        module.classList.add('is-loading');
                        if (loadBtn) loadBtn.disabled = true;

                        // Filter change wipes the grid up-front so the user
                        // sees the old category clear immediately, then the
                        // spinner, then the new cards. Load More keeps its
                        // existing cards and appends the next page below.
                        if (replace) grid.innerHTML = '';

                        $.ajax({
                            url: vars.ppd_ajax_url,
                            type: 'POST',
                            data: {
                                action:   'lmar_load_posts',
                                nonce:    vars.nonce,
                                category: currentFilter,
                                season:   season,
                                offset:   offset,
                                count:    count,
                            },
                        }).done(res => {
                            if (!res || !res.success) return;

                            if (replace) {
                                grid.innerHTML = res.data.html;
                            } else {
                                grid.insertAdjacentHTML('beforeend', res.data.html);
                            }

                            if (loadBtn) {
                                const newOffset = offset + count;
                                loadBtn.dataset.offset = newOffset;
                            }

                            if (footer) {
                                footer.classList.toggle('is-hidden', !res.data.has_more);
                            }
                        }).always(() => {
                            isLoading = false;
                            module.classList.remove('is-loading');
                            if (loadBtn) loadBtn.disabled = false;
                        });
                    };

                    // Apply a filter slug to the UI + grid. Optionally writes
                    // the slug to the URL so deep links / refreshes preserve
                    // the active filter. Used by both pill clicks and the
                    // popstate handler below.
                    const applyFilter = (slug, { writeUrl } = { writeUrl: false }) => {
                        const next = slug || 'all';
                        if (next === currentFilter) return;
                        currentFilter = next;
                        filterBtns.forEach(b => {
                            b.classList.toggle('is-active', (b.dataset.filter || 'all') === next);
                        });
                        if (loadBtn) loadBtn.dataset.offset = count;
                        if (writeUrl) {
                            const url = new URL(window.location.href);
                            if (next === 'all') {
                                url.searchParams.delete('category');
                            } else {
                                url.searchParams.set('category', next);
                            }
                            history.pushState({ category: next }, '', url.toString());
                        }
                        fetchPosts(0, true);
                    };

                    // Filter pill click
                    filterBtns.forEach(btn => {
                        btn.addEventListener('click', () => {
                            if (btn.classList.contains('is-active')) return;
                            applyFilter(btn.dataset.filter, { writeUrl: true });
                        });
                    });

                    // Back/forward — re-apply whatever the URL now says.
                    window.addEventListener('popstate', () => {
                        const urlCat = new URL(window.location.href).searchParams.get('category') || 'all';
                        applyFilter(urlCat);
                    });

                    // Load more
                    if (loadBtn) {
                        loadBtn.addEventListener('click', () => {
                            const offset = parseInt(loadBtn.dataset.offset, 10) || count;
                            fetchPosts(offset, false);
                        });
                    }
                });
            }

            /**
             * Calendar — mobile month navigation (prev/next arrows).
             * On desktop (>=1024px), all months are visible via CSS.
             */
            static initCalendars() {
                document.querySelectorAll('.js-calendar').forEach(calendar => {
                    const track  = calendar.querySelector('.calendar__track');
                    const months = track ? Array.from(track.querySelectorAll('.calendar-month')) : [];
                    if (!months.length) return;

                    // Find index of the current month (Y-m of today), fall back to first
                    const todayYM = (() => {
                        const d = new Date();
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    })();
                    let activeIdx = months.findIndex(m => m.dataset.month === todayYM);
                    if (activeIdx < 0) activeIdx = 0;

                    const setActive = (idx) => {
                        months.forEach((m, i) => m.classList.toggle('is-active', i === idx));
                        activeIdx = idx;
                        // Toggle nav button disabled states on the ACTIVE month only
                        const active = months[idx];
                        const prev = active.querySelector('.js-calendar-prev');
                        const next = active.querySelector('.js-calendar-next');
                        if (prev) prev.style.visibility = idx === 0 ? 'hidden' : 'visible';
                        if (next) next.style.visibility = idx === months.length - 1 ? 'hidden' : 'visible';
                    };

                    setActive(activeIdx);

                    // Wire prev/next on every month (the currently-active one intercepts events)
                    months.forEach((m, idx) => {
                        const prev = m.querySelector('.js-calendar-prev');
                        const next = m.querySelector('.js-calendar-next');
                        if (prev) prev.addEventListener('click', () => {
                            if (activeIdx > 0) setActive(activeIdx - 1);
                        });
                        if (next) next.addEventListener('click', () => {
                            if (activeIdx < months.length - 1) setActive(activeIdx + 1);
                        });
                    });
                });
            }

            /**
             * Image Carousel — Slick on all sizes, no arrows/dots, swipe-enabled.
             * Integer slidesToShow avoids the fractional-snap jumping bug;
             * peek is provided by the wrapper's padding on the right.
             */
            static initImageCarousels() {
                const $tracks = $('.js-image-carousel');
                if (!$tracks.length || typeof $.fn.slick === 'undefined') return;

                $tracks.each(function () {
                    const $track = $(this);
                    if ($track.hasClass('slick-initialized')) return;
                    $track.slick({
                        slidesToShow: 1,
                        slidesToScroll: 1,
                        arrows: false,
                        dots: false,
                        infinite: true,
                        centerMode: true,
                        centerPadding: '26.5%',
                        swipeToSlide: true,
                        autoplay: true,
                        autoplaySpeed: 4000,
                        pauseOnHover: false,
                        pauseOnFocus: false,
                        pauseOnDotsHover: false,
                        responsive: [
                            { breakpoint: 768, settings: { centerPadding: '16px' } },
                        ],
                    });
                });
            }

            /**
             * Reviews marquee — rAF-driven seamless scroll.
             * Single track with one set of cards. JS clones the set to fill 2× viewport,
             * translates the track, and resets cleanly when it crosses one set width.
             */
            static initReviewsMarquee() {
                document.querySelectorAll('.js-reviews-marquee').forEach(wrap => {
                    const track = wrap.querySelector('.reviews__track');
                    if (!track) return;
                    const sets = track.querySelectorAll('.reviews__set');
                    if (!sets.length) return;

                    const firstSet = sets[0];
                    const section  = wrap.closest('.reviews');
                    const speed    = parseFloat(section?.style.getPropertyValue('--reviews-speed')) || 50;

                    // In RTL the track lays out right-to-left, so translating
                    // by a negative X drags content off the screen instead of
                    // looping it. Flip the sign so the marquee scrolls in the
                    // natural reading direction either way.
                    const dirSign = getComputedStyle(track).direction === 'rtl' ? 1 : -1;

                    let offset   = 0;
                    let lastTime = 0;
                    let setWidth = 0;
                    let running  = true;

                    const measure = () => { setWidth = firstSet.offsetWidth; };

                    const ensureFill = () => {
                        measure();
                        if (setWidth === 0) return;
                        const needed = Math.ceil((window.innerWidth * 2) / setWidth) + 1;
                        const current = track.querySelectorAll('.reviews__set').length;
                        for (let i = current; i < needed; i++) {
                            const clone = firstSet.cloneNode(true);
                            clone.setAttribute('aria-hidden', 'true');
                            track.appendChild(clone);
                        }
                    };

                    ensureFill();
                    measure();

                    const tick = (timestamp) => {
                        if (!running) return;
                        if (!lastTime) lastTime = timestamp;
                        const delta = (timestamp - lastTime) / 1000;
                        lastTime = timestamp;

                        offset += speed * delta;
                        if (setWidth > 0 && offset >= setWidth) offset -= setWidth;

                        track.style.transform = `translate3d(${dirSign * offset}px, 0, 0)`;
                        requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);

                    let resizeTimer;
                    window.addEventListener('resize', () => {
                        clearTimeout(resizeTimer);
                        resizeTimer = setTimeout(() => {
                            ensureFill();
                            measure();
                        }, 200);
                    });

                    // Pause on hover for readability
                    wrap.addEventListener('mouseenter', () => { running = false; });
                    wrap.addEventListener('mouseleave', () => {
                        running  = true;
                        lastTime = 0;
                        requestAnimationFrame(tick);
                    });
                });
            }

            /**
             * Weather module — refresh card values + "Updated X" label via
             * AJAX on load so cached page HTML doesn't serve stale numbers.
             */
            static initWeather() {
                const modules = document.querySelectorAll('.js-weather');
                if (!modules.length) return;

                const vars = window.ajax_var || {};
                if (!vars.ppd_ajax_url) return;

                $.ajax({
                    url:   vars.ppd_ajax_url,
                    type:  'POST',
                    cache: false,
                    data:  { action: 'lmar_weather_data' },
                }).done(res => {
                    if (!res || !res.success || !res.data) return;

                    modules.forEach(mod => {
                        Object.keys(res.data).forEach(key => {
                            const nodes = mod.querySelectorAll(`[data-weather-field="${key}"]`);
                            nodes.forEach(n => {
                                const val = res.data[key];
                                if (val !== null && val !== undefined) n.textContent = val;
                            });
                        });
                    });
                });

                App.initWeatherInfoModals();
            }

            /**
             * Weather info popups — clicking the ⓘ icon opens a centred modal
             * with editable copy (ACF). Click-outside, close button, and Esc
             * all dismiss. Adds/removes body scroll lock via .no-scroll.
             */
            static initWeatherInfoModals() {
                const triggers = document.querySelectorAll('[data-weather-info]');
                if (!triggers.length) return;

                const open = (modal) => {
                    modal.classList.add('is-open');
                    modal.setAttribute('aria-hidden', 'false');
                    document.body.classList.add('no-scroll');
                };

                const close = (modal) => {
                    modal.classList.remove('is-open');
                    modal.setAttribute('aria-hidden', 'true');
                    if (!document.querySelector('.weather-modal.is-open')) {
                        document.body.classList.remove('no-scroll');
                    }
                };

                triggers.forEach(btn => {
                    const key   = btn.dataset.weatherInfo;
                    const modal = document.querySelector(`[data-weather-modal="${key}"]`);
                    if (!modal) return;

                    btn.addEventListener('click', () => open(modal));

                    modal.querySelectorAll('[data-weather-modal-close]').forEach(el => {
                        el.addEventListener('click', () => close(modal));
                    });
                });

                document.addEventListener('keydown', (e) => {
                    if (e.key !== 'Escape') return;
                    document.querySelectorAll('.weather-modal.is-open').forEach(close);
                });
            }

            /**
             * Trail cards (list layout) — each card has its own toggle that
             * reveals the description. Independent open state per card (not
             * one-at-a-time).
             */
            static initTrailCards() {
                document.querySelectorAll('.trails--list .trail-card').forEach(card => {
                    const toggle = card.querySelector('.trail-card__toggle');
                    const panel  = card.querySelector('.trail-card__desc-panel');
                    if (!toggle || !panel) return;

                    const inner = panel.querySelector('.trail-card__desc');
                    if (!inner) return;

                    toggle.addEventListener('click', () => {
                        const willOpen = !card.classList.contains('is-open');

                        if (willOpen) {
                            card.classList.add('is-open');
                            toggle.setAttribute('aria-expanded', 'true');
                            panel.style.height = inner.offsetHeight + 'px';
                            panel.addEventListener('transitionend', function onEnd(e) {
                                if (e.propertyName !== 'height') return;
                                panel.style.height = 'auto';
                                panel.removeEventListener('transitionend', onEnd);
                            });
                        } else {
                            panel.style.height = panel.scrollHeight + 'px';
                            panel.getBoundingClientRect(); // force reflow
                            panel.style.height = '0px';
                            card.classList.remove('is-open');
                            toggle.setAttribute('aria-expanded', 'false');
                        }
                    });
                });
            }

            /**
             * FAQ Accordion — one-open-at-a-time within each .js-accordion list.
             *
             * Uses a <button> trigger; .accordion__answer starts height:0,
             * overflow:hidden, and we animate its height to scrollHeight on
             * open (and back to 0 on close).
             */
            static initAccordions() {
                const openItem = (item) => {
                    const answer = item.querySelector(':scope > .accordion__answer');
                    const inner  = answer && answer.querySelector('.accordion__answer-inner');
                    const trigger = item.querySelector(':scope > .accordion__trigger');
                    if (!answer || !inner) return;

                    item.classList.add('is-open');
                    if (trigger) trigger.setAttribute('aria-expanded', 'true');
                    answer.style.height = inner.offsetHeight + 'px';
                    answer.addEventListener('transitionend', function onEnd(e) {
                        if (e.propertyName !== 'height') return;
                        answer.style.height = 'auto';
                        answer.removeEventListener('transitionend', onEnd);
                    });
                };

                const closeItem = (item) => {
                    const answer = item.querySelector(':scope > .accordion__answer');
                    const trigger = item.querySelector(':scope > .accordion__trigger');
                    if (!answer) return;

                    // If currently at auto, snap to the measured pixel height first
                    // so the transition has a starting point.
                    answer.style.height = answer.scrollHeight + 'px';
                    answer.getBoundingClientRect(); // force reflow
                    answer.style.height = '0px';
                    item.classList.remove('is-open');
                    if (trigger) trigger.setAttribute('aria-expanded', 'false');
                };

                document.querySelectorAll('.js-accordion .accordion__item').forEach(item => {
                    const trigger = item.querySelector(':scope > .accordion__trigger');
                    if (!trigger) return;

                    trigger.addEventListener('click', (e) => {
                        e.preventDefault();

                        if (item.classList.contains('is-open')) {
                            closeItem(item);
                            return;
                        }

                        // Close any siblings in the same list first
                        const list = item.closest('.js-accordion');
                        if (list) {
                            list.querySelectorAll(':scope > .accordion__item.is-open').forEach(other => {
                                if (other !== item) closeItem(other);
                            });
                        }

                        openItem(item);
                    });
                });
            }
        }

        $(document).ready(() => {
            App.init();
        });
    })();
})(window.jQuery);
